import { useMemo } from 'react';
import { useSocialStore } from '../stores/socialStore';
import { useNotificationPrefsStore } from '../stores/notificationPrefsStore';
import { useWhatsNewStore } from '../stores/whatsNewStore';
import { useGiftStore } from '../stores/giftStore';
import { categoryForType } from '../utils/notificationCategories';
import { mergeFeed } from '../utils/notificationFeed';
import { isUnread, releaseCreatedAt } from '../utils/whatsNew';
import { giftStatus } from '../utils/coinGift';
import type { NotificationFeedItem, ReleaseNotice } from '../types';

// The notifications the user actually wants to see — the merged center (social
// notifications + notices from the app + What's new releases + Coin gifts, newest
// first) filtered by their in-app category preferences (Settings → Notifications).
// Subscribes to `inApp` so toggling a category re-filters live. Used by every
// notification surface (bell, panel, header) so muting is consistent, without
// socialStore having to know about preferences.
//
// ⚠️ App notices and gifts have no category, so they always show: they carry
// replies and offers waiting on an answer, which must not be mutable into silence.
// Releases CAN be muted ("App updates") — nothing in them needs an answer.
export function useVisibleNotifications(): NotificationFeedItem[] {
  const notifications = useSocialStore((s) => s.notifications);
  const appNotifications = useSocialStore((s) => s.appNotifications);
  const cursor = useSocialStore((s) => s.notificationsCursor);
  const hasMore = useSocialStore((s) => s.hasMoreNotifications);
  const releases = useWhatsNewStore((s) => s.releases);
  const seen = useWhatsNewStore((s) => s.seen);
  const gifts = useGiftStore((s) => s.gifts);
  const inApp = useNotificationPrefsStore((s) => s.inApp);
  return useMemo(() => {
    // ⚠️ Releases and gifts are all here at once; the table rows arrive a page at
    // a time. One older than the last loaded row would sit ABOVE rows not loaded
    // yet — so while more pages remain, only those inside the loaded window show,
    // and older ones arrive with the page that reaches them.
    const floor = hasMore && cursor ? Date.parse(cursor.createdAt) : null;
    const inWindow = (createdAt: string) => floor === null || Date.parse(createdAt) >= floor;

    const releaseItems: ReleaseNotice[] = inApp.updates
      ? releases
          .filter((r) => inWindow(releaseCreatedAt(r)))
          .map((r) => ({
            id: `release:${r.version}`,
            version: r.version,
            date: r.date,
            notes: r.notes,
            createdAt: releaseCreatedAt(r),
            read: !isUnread(r, seen),
          }))
      : [];

    // ⭐ A gift waiting for an answer is PINNED to the top (owner's design): it asks
    // something of the person and expires in 7 days — never hidden by paging.
    // Answered and expired gifts fall into the timeline like anything else.
    const now = Date.now();
    const pending = gifts.filter((g) => giftStatus(g, now) === 'pending');
    const settled = gifts.filter((g) => giftStatus(g, now) !== 'pending' && inWindow(g.createdAt));

    return [
      ...mergeFeed([], [], [], pending),
      ...mergeFeed(
        notifications.filter((n) => {
          const cat = categoryForType(n.type);
          return cat ? inApp[cat] : true; // unknown types always show
        }),
        appNotifications,
        releaseItems,
        settled,
      ),
    ];
  }, [notifications, appNotifications, cursor, hasMore, releases, seen, gifts, inApp]);
}

/** Gifts still waiting for an answer — "Mark all read" can't clear these. */
export function usePendingGiftCount(): number {
  const gifts = useGiftStore((s) => s.gifts);
  return useMemo(() => {
    const now = Date.now();
    return gifts.filter((g) => giftStatus(g, now) === 'pending').length;
  }, [gifts]);
}

/**
 * The badge: EVERY unread notification (not just the rows loaded), less the
 * muted categories, plus unread releases unless "App updates" is off, plus every
 * gift still waiting for an answer (not mutable — it needs one).
 */
export function useUnreadNotificationCount(): number {
  const unread = useSocialStore((s) => s.unreadNotifications);
  const releases = useWhatsNewStore((s) => s.releases);
  const seen = useWhatsNewStore((s) => s.seen);
  const inApp = useNotificationPrefsStore((s) => s.inApp);
  const waitingGifts = usePendingGiftCount();
  return useMemo(() => {
    const rows = Object.values(unread).filter((kind) => {
      const cat = categoryForType(kind);
      return cat ? inApp[cat] : true;
    }).length;
    const updates = inApp.updates ? releases.filter((r) => isUnread(r, seen)).length : 0;
    return rows + updates + waitingGifts;
  }, [unread, releases, seen, inApp, waitingGifts]);
}
